import { isBookFlowSettingsEnabled } from '@wix/bookings-calendar-catalog-viewer-mapper';
import {
  AUTO_OPEN_PREFERENCES_MODAL_STORAGE_KEY,
  BookingsQueryParams,
  getUrlQueryParamValue,
  mapBasketToCalendarSelections,
  storeCalendarSelections,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { navigateToCalendarPage } from '@wix/bookings-catalog-calendar-viewer-navigation';
import { Basket } from '@wix/bookings-services-preferences-modal';
import { ActionFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { CalendarState } from '../../controller';
import { CloseDialogAction } from '../closeDialog/closeDialog';

export type OnServicesPreferencesConfirm = (basket: Basket) => void;

export const createOnServicesPreferencesConfirm =
  (
    { context }: ActionFactoryParams<CalendarState, CalendarContext>,
    closeDialog: CloseDialogAction,
  ): OnServicesPreferencesConfirm =>
  async (basket) => {
    const {
      reloadWidget,
      experiments,
      bookingsSettings,
      isDateAndTimeViewMode,
      flowAPI: {
        controllerConfig: { wixCodeApi, platformAPIs },
      },
    } = context;

    const { session } = platformAPIs.storage;

    const calendarSelections = mapBasketToCalendarSelections(basket);
    storeCalendarSelections({ calendarSelections, session });

    if (
      isBookFlowSettingsEnabled({
        experiments,
        bookingsSettings,
      })
    ) {
      const shouldNavigate = shouldNavigateToCalendar(
        basket,
        isDateAndTimeViewMode,
      );

      if (shouldNavigate) {
        const timezoneFromQueryParam = getUrlQueryParamValue(
          wixCodeApi,
          BookingsQueryParams.TIMEZONE,
        );
        const referralFromQueryParam = getUrlQueryParamValue(
          wixCodeApi,
          BookingsQueryParams.REFERRAL,
        );
        const resourceFromQueryParam = getUrlQueryParamValue(
          wixCodeApi,
          BookingsQueryParams.RESOURCE,
        );
        const locationFromQueryParam = getUrlQueryParamValue(
          wixCodeApi,
          BookingsQueryParams.LOCATION,
        );
        const bookingIdFromQueryParam = getUrlQueryParamValue(
          wixCodeApi,
          BookingsQueryParams.BOOKING_ID,
        );

        await navigateToCalendarPage(wixCodeApi, platformAPIs, {
          serviceSlugOrBasket: basket,
          navigationContext: {
            skipPreferencesModal: true,
            ...(timezoneFromQueryParam && { timezone: timezoneFromQueryParam }),
            ...(referralFromQueryParam && { referral: referralFromQueryParam }),
            ...(resourceFromQueryParam && { resource: resourceFromQueryParam }),
            ...(locationFromQueryParam && { location: locationFromQueryParam }),
            ...(bookingIdFromQueryParam && {
              bookingId: bookingIdFromQueryParam,
            }),
          },
        });
      } else {
        wixCodeApi.location.queryParams.add({
          [BookingsQueryParams.SKIP_PREFERENCES_MODAL]: 'true',
        });
        closeDialog();
        reloadWidget();
      }
    } else {
      session.removeItem(AUTO_OPEN_PREFERENCES_MODAL_STORAGE_KEY);
      closeDialog();
      reloadWidget();
    }
  };

const shouldNavigateToCalendar = (
  basket: Basket,
  isDateAndTimeViewMode: boolean,
): boolean => {
  const hasMultipleItems = basket.selectedServicesPreferences.length > 1;
  const hasSingleItem = basket.selectedServicesPreferences.length === 1;

  // Navigate if:
  // 1. Multiple items and not in date-time view mode, OR
  // 2. Single item and in date-time view mode
  return (
    (hasMultipleItems && !isDateAndTimeViewMode) ||
    (hasSingleItem && isDateAndTimeViewMode)
  );
};
